import { useState, useEffect } from 'react';
import { coreAPI } from '../../lib/api';
import type { UserProfile } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

interface UserBuyerCodesFieldProps {
  /** The user whose ERP buyer codes are being edited. */
  selectedUser: UserProfile;
}

/**
 * Buyer-codes editor for customer material controllers, shown in the User
 * Management user modal.
 *
 * Comma-separated input, normalised to a string array on save. When a user has
 * codes, purchasing list views default to "my suppliers" (rows whose PO line
 * carries one of these codes), with a toggle to all suppliers.
 *
 * Extracted from UserDetailsModal to keep that file within the 500-line limit.
 */
export function UserBuyerCodesField({ selectedUser }: UserBuyerCodesFieldProps) {
  const [buyerCodesInput, setBuyerCodesInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // JSONField: a stray non-list value ({} on some imported rows) must not crash the modal.
    const codes: unknown = selectedUser.buyer_codes;
    setBuyerCodesInput(Array.isArray(codes) ? codes.join(', ') : '');
  }, [selectedUser.id, selectedUser.buyer_codes]);

  const save = async () => {
    setSaving(true);
    try {
      const codes = buyerCodesInput.split(',').map((c) => c.trim()).filter(Boolean);
      await coreAPI.updateManagedUserProfile(selectedUser.id, { buyer_codes: codes });
      toast.success('Buyer codes updated');
    } catch (err: unknown) {
      console.error('Failed to save buyer codes:', err);
      toast.error('Failed to save buyer codes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="text-sm font-medium text-muted-foreground">Buyer Codes</div>
      <div className="flex items-center gap-2 mt-1">
        <input
          value={buyerCodesInput}
          onChange={(e) => setBuyerCodesInput(e.target.value)}
          placeholder="e.g. 01, 02, 24"
          disabled={!selectedUser.can_manage_user}
          className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
        />
        {selectedUser.can_manage_user && (
          <Button size="sm" variant="outline" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Users with codes see only their suppliers by default (they can toggle to all).
      </p>
    </div>
  );
}

export default UserBuyerCodesField;
